import * as THREE from 'three';
import { getRect, getCameraWorldTransform } from '../../modules/player';
import { get, getWorldTransform, set } from '../../modules/scene';
import { getSelectedNodes } from '../../modules/selection';
import { drawScaleGizmo, highlightAxis, getViewScale } from './gizmos';
import getProjectedPoint from './utils/getProjectedPoint';
import getBoundingRadius from './utils/getBoundingRadius';
const { max, pow, PI, sin, min, sqrt } = Math;

const EPS = 0.000001;

export default function nodeScale(store, translator) {
  let activeAxis = false;

  return {
    label: 'Scale Node',
    enabled: true,
    display: {
      playerTools: false,
    },
    options: {},

    tool: {
      hover: function (ev) {
        if (!this.active) return;

        activeAxis = highlightAxis(translator, ev, activeAxis);
      },

      drag: function (ev) {
        if (ev.which !== 1 || !this.active || ev.metaKey) return false;

        return {
          momentum: !activeAxis,
          handle: function (ev) {
            scaleNode(store, ev, activeAxis);
          },
        };
      },
    },

    widget: {
      draw: function (manipulatorsObject) {
        manipulatorsObject.add(drawScaleGizmo());
        const scale = getViewScale(store);
        manipulatorsObject.scale.set(scale, scale, scale);
      },

      position: function (manipulatorsObject) {
        const selectedNodes = getSelectedNodes(store);
        if (!selectedNodes.length) {
          manipulatorsObject.visible = false;
          return;
        }

        manipulatorsObject.visible = true;
        const manipId = selectedNodes[0];
        const worldTransform = getWorldTransform(store, manipId);
        const position = new THREE.Vector3();
        position.setFromMatrixPosition(worldTransform);
        manipulatorsObject.position.set(position.x, position.y, position.z);

        const scale = getViewScale(store);
        manipulatorsObject.scale.set(scale, scale, scale);
      },
    },
  };
}

function scaleNode(store, ev, restrictToAxis) {
  const rect = getRect(store);
  const selectedNodes = getSelectedNodes(store);
  if (!selectedNodes.length) return;
  const id = selectedNodes[0];

  const nodeGraphScale = get(store, { id, plug: 'Transform', property: 'scale' });
  if (!nodeGraphScale) return;
  const radius = max(getBoundingRadius(store, id), EPS) || EPS;

  const delta = ev.delta || (ev.deltaX / 30) - (ev.deltaY / 30);
  const deltaZoom = max(min(2 * delta / rect.y / radius, 0.001), -0.001);
  const decayZoomFactor = pow(2, deltaZoom * 100);

  const nodeMatrix = getWorldTransform(store, id);
  const nodePosition = new THREE.Vector3().setFromMatrixPosition(nodeMatrix);
  const nodeScale = new THREE.Vector3().copy(nodeGraphScale);
  const cameraWorldMatrix = getCameraWorldTransform(store);

  if (!restrictToAxis) {
    const oldScale = max(nodeScale.length(), EPS);
    nodeScale.setLength(oldScale * decayZoomFactor);
  } else {
    const projVector = new THREE.Vector3(0, 0, 0);
    projVector[restrictToAxis] = 1;
    const projectedMouse = getProjectedPoint(store, id, ev.deltaX, ev.deltaY);
    projectedMouse.projectOnVector(projVector);

    const targToCam = new THREE.Vector3().setFromMatrixPosition(cameraWorldMatrix).sub(nodePosition);
    const origin = new THREE.Vector3(0, 0, 0);
    const plane = new THREE.Plane().setFromCoplanarPoints(targToCam, projVector, origin);
    projectedMouse.projectOnPlane(plane.normal);

    const camToMove = new THREE.Vector3().subVectors(projectedMouse, targToCam);
    projectedMouse.projectOnVector(projVector).normalize();
    const alpha = targToCam.angleTo(projectedMouse);
    const beta = PI - targToCam.angleTo(camToMove);
    const gamma = PI - alpha - beta;
    const axis = targToCam.length() * sin(beta) / sin(gamma);
    projectedMouse.multiplyScalar(axis);

    projVector.set(1, 1, 1);

    projVector[restrictToAxis] = 1 + 2 * projectedMouse[restrictToAxis] / nodeScale[restrictToAxis];
    nodeScale.multiply(projVector);
  }

  // FIXME: restrict axis
  //console.log('scaleToAxis', restrictToAxis);
  store.dispatch(set({ id, plug: 'Transform', property: 'scale' }, nodeScale));
}




// WEBPACK FOOTER //
// ./src/translators/tools/nodeScale.js